import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const PremiumBackground = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    // 1. Initial Setup
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x111111, 0.0035); // Matches the new dark background

    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    // Position up and looking slightly down
    camera.position.set(0, 40, 100);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: "high-performance" });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    if (mountRef.current) {
      mountRef.current.appendChild(renderer.domElement);
    }

    // 3. Create Particle Wave (High-End Aesthetic)
    // We will use highly dense points to create a flowing topographical field
    const geometry = new THREE.PlaneGeometry(350, 350, 120, 120);
    // Rotate Plane so it lies flat on XZ
    geometry.rotateX(-Math.PI / 2);

    // Give each particle a basic size offset
    const count = geometry.attributes.position.count;
    const randoms = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      randoms[i] = Math.random();
    }
    geometry.setAttribute('aRandom', new THREE.BufferAttribute(randoms, 1));

    // Custom Shader for Magnetism and Sine Waves
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uIntro: { value: 0.0 }, // Intro fade multiplier
        uMouse: { value: new THREE.Vector3(0, -999, 0) }, // Starts off-screen safely
        uColor: { value: new THREE.Color(0xffff3d) }, // Exact Logo Yellow
      },
      vertexShader: `
        uniform float uTime;
        uniform vec3 uMouse;
        
        attribute float aRandom;
        
        varying float vElevation;
        varying float vAlpha;

        void main() {
          vec3 pos = position;

          // 1. Base flowing topographic wave (Ocean-like)
          float wave = sin(pos.x * 0.04 + uTime * 0.4) * 4.0;
          wave += sin(pos.z * 0.03 + uTime * 0.25) * 4.0;
          wave += sin((pos.x + pos.z) * 0.02 + uTime * 0.5) * 2.0;

          // 2. Mouse Repulsion/Magnetic Field effect
          float distanceToMouse = distance(pos.xz, uMouse.xz);
          float magnetRadius = 70.0; // Much wider, softer magnetic field
          float magnetStrength = 18.0;
          
          float influence = smoothstep(magnetRadius, 0.0, distanceToMouse);
          // Soften the curve to make it look like a gentle hill rather than a spike
          influence = pow(influence, 1.5);
          
          // Elevate particles near the mouse dynamically
          pos.y += wave + (influence * magnetStrength);

          // Pass to fragment for color/opacity logic
          vElevation = pos.y;
          vAlpha = smoothstep(-10.0, 20.0, pos.y); // Higher points fade in stronger

          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          
          // Dynamic pixel size based on height, randomization and depth
          gl_PointSize = (12.0 * aRandom + 4.0) * (30.0 / -mvPosition.z);
          // Highlight the points elevated by the mouse
          gl_PointSize += influence * 8.0;

          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float uIntro;

        varying float vElevation;
        varying float vAlpha;

        void main() {
          // Circular soft particle
          float dist = distance(gl_PointCoord, vec2(0.5));
          if (dist > 0.5) discard;

          // Smooth radial gradient for glow
          float strength = (0.5 - dist) * 2.0;
          
          // Higher points (peaks of waves and mouse) appear brighter and more opaque
          float finalOpacity = strength * (vAlpha * 0.6 + 0.2) * uIntro;

          // Slightly shift color hue at peaks (add white)
          vec3 peakColor = mix(uColor, vec3(1.0), smoothstep(10.0, 20.0, vElevation));

          gl_FragColor = vec4(peakColor, finalOpacity);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    // 3. Mouse Interaction Raycasting
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2(-999, -999);
    
    // We use an invisible plane exactly where the particles usually rest (y=0) to calculate ray intersection
    const intersectionPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

    const onPointerMove = (event) => {
      // Normalize mouse coordinates for raycasting
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    document.addEventListener('pointermove', onPointerMove, false);

    // 4. Animation Loop
    const clock = new THREE.Clock();
    let animationFrameId;
    let isMouseInitialized = false;

    const renderScene = () => {
      const elapsedTime = clock.getElapsedTime();

      // Intro Fade-in logic (Smooth exponential approaching)
      if (material.uniforms.uIntro.value < 1.0) {
        // Fast but smooth fade in over 2 seconds
        material.uniforms.uIntro.value += (1.0 - material.uniforms.uIntro.value) * 0.02 + 0.005;
        if (material.uniforms.uIntro.value > 0.99) material.uniforms.uIntro.value = 1.0;
      }

      // Update shader time
      material.uniforms.uTime.value = elapsedTime;

      // Raycast to find intersection with the mathematical plane
      raycaster.setFromCamera(mouse, camera);
      const targetPoint = new THREE.Vector3();
      const intersection = raycaster.ray.intersectPlane(intersectionPlane, targetPoint);
      
      if (intersection) {
        if (!isMouseInitialized) {
          // Snap instantly to cursor on first active movement to avoid a giant flying wave
          material.uniforms.uMouse.value.copy(intersection);
          isMouseInitialized = true;
        } else {
          // Smoothly lerp towards subsequent mouse positions, extremely slow and luxurious trail
          material.uniforms.uMouse.value.lerp(intersection, 0.03);
        }
      }

      // Majestic cinematic rotation of the whole field, slowly swinging
      particles.rotation.y = elapsedTime * 0.015; // Slower rotation

      // Parallax camera to add depth (even softer motion)
      camera.position.x += (mouse.x * 8 - camera.position.x) * 0.015;
      camera.position.y += ((mouse.y * 8 + 40) - camera.position.y) * 0.015;
      camera.lookAt(scene.position);

      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(renderScene);
    };

    renderScene();

    // 5. Handle Window Resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // 6. Cleanup
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      if (mountRef.current && renderer.domElement) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        mountRef.current.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div 
      ref={mountRef} 
      style={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100vw', 
        height: '100vh', 
        zIndex: 0, 
        pointerEvents: 'auto', // Important so the canvas absorbs mouse events properly if needed, but App.css might overlay it
        overflow: 'hidden'
      }} 
    />
  );
};

export default PremiumBackground;
